import React, { useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { mediaUrl, PLACEHOLDER_COVER } from "@/lib/media";
import { toast } from "sonner";
import { ImagePlus, Trash2, Loader2, Eye } from "lucide-react";
import { L, SaveBtn, Toggle, Card, SectionTitle, DateInput, fmtDate } from "./shared";

export default function OverviewTab({ pid, cfg, meta, onSaved }) {
  const [f, setF] = useState(() => ({ ...cfg.overview }));
  const [saving, setSaving] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (patch) => setF(x => ({ ...x, ...patch }));

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/staff/client-config/${pid}`, f);
      onSaved(data); toast.success("Configurare portal salvată");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  }

  async function uploadCover(file) {
    if (!file) return;
    setCoverBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/staff/client-config/${pid}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get(`/staff/client-config/${pid}`); onSaved(data); toast.success("Cover actualizat");
    } catch (e) { toast.error(apiErr(e)); } finally { setCoverBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  }

  async function removeCover() {
    if (!window.confirm("Ștergi imaginea de cover?")) return;
    setCoverBusy(true);
    try {
      await api.delete(`/staff/client-config/${pid}/cover`);
      const { data } = await api.get(`/staff/client-config/${pid}`); onSaved(data); toast.success("Cover șters");
    } catch (e) { toast.error(apiErr(e)); } finally { setCoverBusy(false); }
  }

  const cover = cfg.project?.cover_image;
  const company = cfg.company;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6" data-testid="cc-overview">
      <div className="space-y-6">
        <Card>
          <SectionTitle sub="Aceste informații apar în antetul proiectului din portalul clientului.">Identitate proiect</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <L label="Nume intern (din Proiecte & Alocări)"><input value={cfg.project?.name || ""} disabled className="inp" /></L>
            <L label="Denumire afișată clientului"><input value={f.display_name} onChange={e => set({ display_name: e.target.value })} data-testid="ov-display-name" className="inp" placeholder={cfg.project?.name} /></L>
            <div className="md:col-span-2"><L label="Descriere proiect"><textarea rows={3} value={f.description} onChange={e => set({ description: e.target.value })} data-testid="ov-description" className="inp" /></L></div>
            <div className="md:col-span-2"><L label="Adresă / Locație"><input value={f.address} onChange={e => set({ address: e.target.value })} data-testid="ov-address" className="inp" /></L></div>
            <L label="Data începerii"><DateInput value={f.start_date || ""} onChange={v => set({ start_date: v })} testid="ov-start-date" /></L>
            <L label="Termen estimat finalizare" hint="Se editează și în tab-ul Status & Health."><input value={fmtDate(cfg.estimated_completion)} disabled className="inp" /></L>
            <L label="Suprafață (afișată)"><input value={f.surface_display} onChange={e => set({ surface_display: e.target.value })} className="inp" placeholder={cfg.project?.surface || "ex: 12.500 mp"} /></L>
            <L label="Valoare (afișată)"><input value={f.value_display} onChange={e => set({ value_display: e.target.value })} className="inp" placeholder={cfg.project?.value || "ex: 8,5 mil. €"} /></L>
          </div>
        </Card>

        <Card>
          <SectionTitle sub="Textul apare în partea de sus a paginii proiectului, sub cover.">Mesaj de bun venit</SectionTitle>
          <textarea rows={3} value={f.welcome_message} onChange={e => set({ welcome_message: e.target.value })} data-testid="ov-welcome" className="inp" placeholder="Ex: Bine ați venit în portalul proiectului. Aici găsiți statusul, deciziile și documentele la zi." />
        </Card>

        <Card>
          <SectionTitle sub="Persoana de contact principală afișată clientului. Echipa extinsă se configurează în tab-ul Echipă.">Project manager & contact</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <L label="Project manager (angajat)">
              <select value={f.pm_id || ""} onChange={e => set({ pm_id: e.target.value })} data-testid="ov-pm" className="inp">
                <option value="">— fără —</option>
                {(meta?.employees || []).map(u => <option key={u.id} value={u.id}>{u.name}{u.job_title ? ` · ${u.job_title}` : ""}</option>)}
              </select>
            </L>
            <L label="Companie (beneficiar)" hint="Se schimbă din tab-ul Acces.">
              <input value={company?.name || cfg.project?.client_name || "— nealocată —"} disabled className="inp" />
            </L>
            <L label="Contact suplimentar — nume"><input value={f.contact_name} onChange={e => set({ contact_name: e.target.value })} className="inp" /></L>
            <L label="Contact — email"><input value={f.contact_email} onChange={e => set({ contact_email: e.target.value })} className="inp" /></L>
            <L label="Contact — telefon"><input value={f.contact_phone} onChange={e => set({ contact_phone: e.target.value })} className="inp" /></L>
          </div>
        </Card>

        <Card>
          <SectionTitle sub="Alege ce secțiuni vede clientul în portal.">Vizibilitate secțiuni</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Toggle checked={f.show_gallery} onChange={v => set({ show_gallery: v })} label="Galerie foto" testid="ov-show-gallery" />
            <Toggle checked={f.show_documents} onChange={v => set({ show_documents: v })} label="Documente" testid="ov-show-documents" />
            <Toggle checked={f.show_team} onChange={v => set({ show_team: v })} label="Echipă & contact" testid="ov-show-team" />
            <Toggle checked={f.show_surface} onChange={v => set({ show_surface: v })} label="Suprafață" />
            <Toggle checked={f.show_value} onChange={v => set({ show_value: v })} label="Valoare proiect" testid="ov-show-value" />
          </div>
        </Card>

        <SaveBtn onClick={save} saving={saving} testid="ov-save" />
      </div>

      <div className="space-y-6">
        <Card className="p-4">
          <SectionTitle sub="Recomandat 1600×900 px, max 15 MB. Se redimensionează automat.">Imagine de cover</SectionTitle>
          <div className="aspect-[16/9] rounded-lg overflow-hidden bg-neutral-100 relative" data-testid="ov-cover-preview">
            <img src={mediaUrl(cover) || PLACEHOLDER_COVER} alt="cover" className="w-full h-full object-cover" />
            {coverBusy && <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => uploadCover(e.target.files?.[0])} data-testid="ov-cover-input" />
          <div className="flex gap-2 mt-3">
            <button onClick={() => fileRef.current?.click()} disabled={coverBusy} data-testid="ov-cover-upload"
              className="inline-flex items-center gap-2 px-3 py-2 bg-ink text-white text-[13px] rounded-lg hover:bg-brand disabled:opacity-60">
              <ImagePlus className="h-4 w-4" /> {cover ? "Schimbă" : "Încarcă"}
            </button>
            {cover && <button onClick={removeCover} disabled={coverBusy} data-testid="ov-cover-delete"
              className="inline-flex items-center gap-2 px-3 py-2 border border-neutral-200 text-[13px] rounded-lg hover:border-red-500 hover:text-red-600 disabled:opacity-60">
              <Trash2 className="h-4 w-4" /> Șterge
            </button>}
          </div>
          <p className="text-[11px] text-ink-muted mt-2">Poți seta orice fotografie din Galerie drept cover (butonul „Setează ca cover").</p>
        </Card>

        <Card className="p-4" data-testid="ov-preview">
          <div className="flex items-center gap-2 mb-3"><Eye className="h-4 w-4 text-brand" /><span className="font-mono text-[10px] uppercase tracking-widest text-brand">Cum vede clientul</span></div>
          <div className="text-[11px] font-mono text-ink-muted">{cfg.project?.code}</div>
          <div className="font-display font-bold text-lg leading-tight text-ink">{f.display_name || cfg.project?.name}</div>
          {company && <div className="text-[12px] text-ink-muted mt-1 flex items-center gap-2">{company.logo_url && <img src={mediaUrl(company.logo_url)} alt="" className="h-5 w-5 object-contain" />}{company.name}</div>}
          <div className="text-[12px] text-ink-muted mt-2 line-clamp-3">{f.description}</div>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            <span className="px-2 py-0.5 rounded bg-neutral-100">{cfg.client_status}</span>
            <span className="px-2 py-0.5 rounded bg-neutral-100">{cfg.client_progress ?? 0}%</span>
            {cfg.pm?.name && <span className="px-2 py-0.5 rounded bg-neutral-100">PM: {cfg.pm.name}</span>}
          </div>
        </Card>
      </div>
    </div>
  );
}
